<!-- Copyright (c) 2022 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="verification-main">
    <div class="verification-main-content">
      <div class="header">
        <div>{{ title }} - {{ sections[currentSectionIndex]['name'] }}</div>
        <img @click="$emit('update:show', false)" class="close-icon" src="https://dsmlib.logistics.systems/Icons/Cancel.png">
      </div>
      <div class="body">
        <div class="sections-indicator">
          <div class="section-number" :style="sectionStyling(i)" v-for="(s, i) in slotsCount" :key="i">{{ i + 1 }}</div>
        </div>
        <div class="body-content" v-for="(s, i) in sections" :key="i" :style="contentStyling(i)">
          <button class="auto-select-button" @click="selectBestMatch">Auto Select Best Match</button>
          
          <div class="body-details">
            <Carousel class="carousel-container" v-if="attachmentsList.length > 0" :viewCount="attachmentsList.length">
              <div class="attachments-section" v-for="(a, i) in attachmentsList" :key="i" :slot="`content${i + 1}`">
                <object class="attachment-container" v-if="viewableFormat.includes(a['link'].split('.').at(-1).toLowerCase()) || a['link'].startsWith('blob')" :data="a['link']" download="false"></object>
                <div class="attachment-container" v-else>
                  <div>File type <b>{{ a['link'].split('.').at(-1).toLowerCase() }}</b> is not viewable.<br>Please use the download button below to download the attachment</div>
                </div>
                <div v-if="attachmentsList.length > 0" class="attachment-details">
                  <div class="arrow-up" @click="showAttachmentDetails = !showAttachmentDetails">
                    <img src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png" alt="" :style="{ transform: `scaleY(${showAttachmentDetails ? '1' : '-1'})` }">
                  </div>
                  <div>{{ i + 1 }} of {{ attachmentsList.length }} attachment(s)</div>
                  <div><b>Filename</b>: {{ a['filename'] }}</div>
                  <div><b>Remarks</b>: {{ a['remarks'] }}</div>
                  <a :href="a['link']" :target="viewableFormat.includes(a['link'].split('.').at(-1).toLowerCase()) || a['link'].startsWith('blob') ? '_blank' : ''">Download Attachment</a>
                </div>
              </div>
            </Carousel>
            <div v-else class="carousel-container">No attachments available</div>

            <div class="row-sections">
              <div class="row-items row-header">
                <div class="column center-text"></div>
                <div class="column center-text">Submitted</div>
                <div class="column center-text">Selected</div>
                <div class="column center-text">Detected</div>
              </div>

              <div v-for="(d, di) in s['verifyData']" :key="di" class="row-items">
                <div class="column">{{ d['column'] }}</div>
                <div class="column" :style="{ fontStyle: `${d['detected'] ? (d['detected'].split('\n').join('') ? 'normal' : 'italic') : 'italic'}`,
                  color: `${d['detected'] ? (d['detected'].split('\n').join('') ? '' : 'red') : 'red'}` }">{{ d['detected'] ? (d['detected'].split('\n').join('') ? d['detected'] : 'Not detected') : 'Not detected' }}</div>
                <div class="column no-wrap">
                  <InputQuery :placeholder="d['column']" :items="d['submittedList']" :itemKey="d['displayKey']" :descKey="d['submittedDesc']"
                    :isRequired="d['isRequired']" :errorMessage.sync="d['errorMessage']" :selectedObject.sync="d['selectedObject']" @selected="matchedManualSelect($event, di)"
                    :addLink="d['addSubmittedRecordLink'] || ''" :canRefresh="d['canRefreshRecords']" @refreshClicked="submittedInputQueryRefreshClicked" />
                  <div class="score-bubble" v-if="d['selectedObject']" :style="{ backgroundColor: `${d['selectedObject']['matchScore'] > 20 ? 'limegreen' :
                        (d['selectedObject']['matchScore'] > 15 ? 'limegreen' :
                          (d['selectedObject']['matchScore'] > 10 ? 'goldenrod' :
                            d['selectedObject']['matchScore'] ? 'indianred' : 'gray'))}` }">
                    {{ d['selectedObject']['matchScore'] == null ? 'Manual Selection' : (d['selectedObject']['matchScore'] > 20 ? 'Very Strong' :
                        (d['selectedObject']['matchScore'] > 15 ? 'Strong' :
                          (d['selectedObject']['matchScore'] > 10 ? 'Medium' :
                            d['selectedObject']['matchScore'] ? 'Weak' : 'No')) + ' Match') }}
                  </div>
                  <div v-if="d['selectedObject'] && d['submittedDetails']">
                    <div v-for="(detail, index) in d['selectedObject'][d['submittedDetails']]" :key="index">
                      <span>{{detail}}</span><br>
                    </div>
                  </div>
                </div>
                <div class="column center-text collision-button">
                  <button @click="openTableBox($event, d)">
                    <div>{{ d['collisions'] ? d['collisions'].length : '0' }} Matches</div>
                    <img src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png" :style="{ transform: `scaleY(${d['showCollisions'] ? '-1' : '1'})` }">
                  </button>
                  <div v-if="d['showCollisions']" class="collision-table" :style="d['tableStyling']">
                    <TableBox :title="d['column']" :tableData="d['collisions']" :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                      :stickColumn="[0]" @buttonClicked="tableButtonClicked(...arguments, di)" :hideColumns="[ 'matchScore' ]" />
                  </div>
                  <div v-if="d['showCollisions']" @click="d['showCollisions'] = false" class="collision-table-background"></div>
                </div>
              </div>
              <slot v-if="i == currentSectionIndex" :name="s['name']"></slot>
            </div>
          </div>
        </div>
      </div>
      <div class="footer">
        <button :disabled="currentSectionIndex == 0" @click="prevNextClicked(false)">Previous</button>
        <button v-if="currentSectionIndex < slotsCount - 1" :disabled="currentSectionIndex == slotsCount - 1" @click="prevNextClicked(true)">Next</button>
        <button v-if="currentSectionIndex == slotsCount - 1" @click="submitClicked">Confirm</button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { doubleMetaphone } from 'double-metaphone';
import Fuse from 'fuse.js';

export default {
  components: {
  },
  data: function() {
    return {
      slotsCount: 0,
      currentSectionIndex: 0,

      showAttachmentDetails: true,
      viewableFormat: [ 'gif', 'jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp', 'png', 'svg', 'pdf', 'txt', 'json', 'mp4' ],
    }
  },
  props: {
    title: { type: String, default: 'Title Here' },
    sections: { type: Array, default: function() { return [] } },
    show: { type: Boolean, default: false },
    attachmentsList: { type: Array, default: function() { return [] } },
    showAttachment: { type: Boolean, default: false },
  },
  computed: {
  },
  methods: {
    sectionStyling: function(index) {
      return {
        backgroundColor: index <= this.currentSectionIndex ? '#b3c4ff' : '',
        color: index <= this.currentSectionIndex ? 'white' : '',
        border: `2px solid ${index <= this.currentSectionIndex ? '#b3c4ff' : 'gray'}`
      };
    },
    contentStyling: function(index) {
      return {
        display: this.currentSectionIndex == index ? '' : 'none'
      }
    },

    openTableBox: function(e, item) {
      let rect = e.target.getBoundingClientRect();

      let isOpen = item['showCollisions'];

      this.$nextTick(() => {
        this.sections.forEach(s => {
          s['verifyData'].forEach(v => {
            v['showCollisions'] = false;
          });
        })

        this.$nextTick(() => {
          let top = rect.top + rect.height;
          let right = window.innerWidth - rect.right;

          item['tableStyling'] = {
            top: `${top}px`,
            right: `${right}px`
          };
          item['showCollisions'] = !isOpen;

          if (item['showCollisions']) {
            let winHeight = window.innerHeight / 2;

            if (top + winHeight > window.innerHeight - 10) {
              let diff = (top + winHeight + 10) - (window.innerHeight);
              item['tableStyling'] = {
                top: `${top - diff}px`,
                right: `${right + rect.width}px`
              }
            }
          }
        });
      })
    },

    recalculateIndex: function(ind, length) {
      let newIndex = 0;
      if (ind >= length) {
        newIndex = ind - length;
        while (newIndex >= length) {
          newIndex -= length;
        }
      } else {
        newIndex = ind;
      }

      return newIndex;
    },

    //#region Component functions
    prevNextClicked: function(next) {
       // All required fields filled in before next but on previous it is not required
      if(!next)
        this.currentSectionIndex--;
      else if(this.checkRequiredFields())
        this.currentSectionIndex++;
      else{
        return;
      }
      // check selectedObject is null or undefined
      const currentSection = this.sections[this.currentSectionIndex];
      const selectBestMatch = currentSection['verifyData'].some(v => !v['selectedObject']);
      
      if (selectBestMatch) {
        this.selectBestMatch(); // auto-select best match for the new section
      }
    },
    submitClicked: function() {
      if (this.checkRequiredFields()) { // All required fields filled in
        let detectedData = {};
        let selectedData = {};
        this.sections.forEach(s => {
          s['verifyData'].forEach(v => {
            detectedData[v['historyKey']] = v['detected'].split('\n')[v['compareIndex']];
            if (v['selectedObject']) {
              selectedData[v['historyKey']]  = v['selectedObject'][v['submittedKey']];
            }
          });
        });

        this.$emit('confirmClicked', detectedData, selectedData);
      }
    },
    checkRequiredFields: function() { // return false if one or more required fields is not filled in
      let notSelected = this.sections[this.currentSectionIndex]['verifyData'].filter(v => v['isRequired'] && !v['selectedObject']);
      if (notSelected.length > 0) {
        notSelected.forEach(n => {
          n['errorMessage'] = `${n['column']} is required!`;
        });
      }
      
      return notSelected.length == 0;
    },
    tableButtonClicked: function(text, data, index) {
      let verifyData = this.sections[this.currentSectionIndex]['verifyData'][index];
      verifyData['selectedObject'] = data;
      verifyData['showCollisions'] = false;
      verifyData['errorMessage'] = '';
    },
    normalizeMatchText: function(value) {
      return String(value == null ? '' : value)
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },
    normalizeFuseText: function(value) {
      // Same preprocessing used by JobFormComponent.bestMatch.
      return String(value == null ? '' : value)
        .split('(')[0]
        .trim()
        .replace(/[^\w\s]|_/g, '')
        .toUpperCase();
    },
    getFuseMatchScores: function(v, value) {
      let query = this.normalizeFuseText(value);
      if (!query) return new Map();

      let search = (v['submittedList'] || []).filter(Boolean).map(item => ({
        location: item,
        preprocessed: this.normalizeFuseText(item[v['submittedNameKey'] || v['submittedDesc']])
      })).filter(item => item.preprocessed);

      try {
        let fuse = new Fuse(search, { includeScore: true, keys: [ 'preprocessed' ] });
        return new Map(
          fuse.search(query)
            .filter(result => Number(result.score) < 0.43)
            .map(result => [ result.item.location, Number(result.score) ])
        );
      } catch (e) {
        return new Map();
      }
    },
    getSubmittedAddress: function(v, submitted) {
      let configuredKey = v['submittedAddressKey'] || v['submittedDetails'];
      let configuredValue = configuredKey ? submitted[configuredKey] : '';
      let values = Array.isArray(configuredValue) ? configuredValue.filter(Boolean) :
        (String(configuredValue || '').trim() ? [ configuredValue ] : []);

      // JobFormComponent stores master addresses in separate fields such as
      // Shipper Add1..Add4, PostCode, City, State and Country. Combine these
      // with submittedDetails because either source can be incomplete.
      let priorities = [
        /(?:^| )ADD(?:RESS)? ?1$/i,
        /(?:^| )ADD(?:RESS)? ?2$/i,
        /(?:^| )ADD(?:RESS)? ?3$/i,
        /(?:^| )ADD(?:RESS)? ?4$/i,
        /(?:^| )ADDRESS$/i,
        /(?:^| )POST ?CODE$/i,
        /(?:^| )CITY$/i,
        /(?:^| )STATE$/i,
        /(?:^| )COUNTRY$/i
      ];
      priorities.forEach(pattern => {
        Object.keys(submitted).forEach(key => {
          if (key !== configuredKey && pattern.test(key.replace(/[_-]+/g, ' ')) && submitted[key]) {
            let fieldValues = Array.isArray(submitted[key]) ? submitted[key] : [ submitted[key] ];
            fieldValues.forEach(value => {
              if (value && !values.some(existing => this.normalizeMatchText(existing) === this.normalizeMatchText(value))) {
                values.push(value);
              }
            });
          }
        });
      });
      return values;
    },
    getMatchInput: function(v) {
      let lines = String(v['detected'] || '').split(/\r?\n/);
      let index = Number(v['compareIndex']);
      index = Number.isInteger(index) && index >= 0 ? index : 0;
      let detected = lines[index] || '';
      let code = String(v['code'] || '').trim().toUpperCase();
      let companyIndex = index;
      let name = v['name'] || detected;

      // Only infer a code when it exists in the submitted code column.
      if (!code) {
        let codes = new Set((v['submittedList'] || []).filter(Boolean).map(item =>
          String(item[v['submittedKey']] == null ? '' : item[v['submittedKey']]).trim().toUpperCase()
        ));
        code = [detected, lines[0]].map(value => String(value || '').trim().toUpperCase())
          .find(value => value && codes.has(value)) || '';
      }

      // A code supplied separately is authoritative only when it also occurs
      // in the submitted text. This prevents a stale/derived v.code from
      // overriding a clearly matching branch address.
      let codeIsVisible = !!code && lines.some(line => String(line).trim().toUpperCase() === code);

      // A document reference can precede the company without itself being
      // a destination code (for example TIACHEPKG / TIARCO CHEMICAL).
      // Only use the next line when it looks like a company, not an address.
      if (!v['name'] && index === 0 && !/\s/.test(detected.trim()) &&
          detected.trim().length <= 24 && lines.length > 1) {
        let nextLine = lines[1].trim();
        if (/^[A-Z][A-Z0-9 .,&()'-]+$/i.test(nextLine) &&
            nextLine.split(/\s+/).length >= 2 &&
            !/^(?:LOT|NO|JALAN|JLN|PERSIARAN|KAWASAN|TEL|FAX)\b/i.test(nextLine) &&
            !/^\d/.test(nextLine)) {
          companyIndex = 1;
          name = nextLine;
        }
      }

      // Preserve compareIndex for history; only separate code/name for scoring.
      if (code && String(detected).trim().toUpperCase() === code) {
        let next = lines.findIndex((line, i) => i > index && line.trim() &&
          !/^(?:TEL(?:EPHONE)?|PHONE|FAX|E-?MAIL)\s*(?::|$)/i.test(line.trim()));
        if (next !== -1) {
          companyIndex = next;
          if (!v['name']) name = lines[next];
        }
      }

      let address = v['address'] != null ? v['address'] : lines.slice(companyIndex + 1);
      return { detected, code, codeIsVisible, name, address };
    },
    cleanMatchAddress: function(value, name, code) {
      return (Array.isArray(value) ? value.join('\n') : String(value || ''))
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => this.normalizeMatchText(line) &&
          !/^(?:TEL(?:EPHONE)?|PHONE|MOBILE|HP|FAX|E-?MAIL|ATTN|ATTENTION|CONTACT)\s*(?::|$)/i.test(line) &&
          this.normalizeMatchText(line) !== this.normalizeMatchText(name) &&
          line.toUpperCase() !== code)
        .join(' ');
    },
    getMatchContact: function(value) {
      return String(value || '').split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => /^(?:TEL(?:EPHONE)?|PHONE|MOBILE|HP|FAX|E-?MAIL|ATTN|ATTENTION|CONTACT)\s*:/i.test(line))
        .map(line => line.replace(/^[^:]+:/, '').trim())
        .filter(Boolean)
        .join(' ');
    },
    getSubmittedContact: function(v, submitted) {
      let values = [];
      let detailsKey = v['submittedDetails'];
      let details = detailsKey ? submitted[detailsKey] : '';
      let detailValues = Array.isArray(details) ? details : [ details ];
      detailValues.forEach(value => {
        let contact = this.getMatchContact(value);
        if (contact) values.push(contact);
      });
      Object.keys(submitted).forEach(key => {
        if (/(?:^| )(?:TEL(?:EPHONE)?|PHONE|MOBILE|HP|FAX|E-?MAIL|ATTN|ATTENTION|CONTACT)$/i.test(key.replace(/[_-]+/g, ' ')) && submitted[key]) {
          values.push(submitted[key]);
        }
      });
      return values.join(' ');
    },
    buildPhoneticMatches: function(v, allHistoricalValue) {
      let input = this.getMatchInput(v);
      let normalize = this.normalizeMatchText;
      let query = normalize(input.name);
      let detected = normalize(input.detected);
      if (!query && !detected && !input.code) return [];

      // Reuse encodings within the row. Empty codes never count as matches.
      let phoneticCache = new Map();
      let encode = value => {
        let text = normalize(value);
        if (!phoneticCache.has(text)) {
          phoneticCache.set(text, {
            soundex: text ? this.soundex(text) : '',
            metaphone: text ? doubleMetaphone(text) : ['', '']
          });
        }
        return phoneticCache.get(text);
      };
      let detectedEncoding = encode(detected);
      let queryEncoding = encode(query);
      let sameMetaphone = (a, b) => a.some(code => code && b.includes(code));
      let similarity = (a, b) => !a || !b ? 0 :
        Math.max(0, 1 - this.levenshtein(a, b) / Math.max(a.length, b.length));

      let historyCounts = Object.create(null);
      let history = (allHistoricalValue || {})[v['historyKey']] || {};
      Object.entries(history).forEach(([text, selections]) => {
        if (!detected || normalize(text) !== detected || !selections || typeof selections !== 'object') return;
        Object.entries(selections).forEach(([code, count]) => {
          let key = String(code).trim().toUpperCase();
          let number = Number(count);
          if (key && Number.isFinite(number) && number > 0) {
            historyCounts[key] = (historyCounts[key] || 0) + number;
          }
        });
      });

      let fuseScores = this.getFuseMatchScores(v, input.name);
      let contact = normalize(this.getMatchContact(v['detected']));
      let address = normalize(this.cleanMatchAddress(input.address, input.name, input.code));
      let numbers = [...new Set(address.split(' ').filter(word => /\d/.test(word)))];
      let postcodes = [...new Set(address.split(' ').filter(word => /^\d{5}$/.test(word)))];
      let canonicalAddressTokens = address.split(' ').map(word => word === 'JLN' ? 'JALAN' : word);
      let streetPrefixes = [ 'JALAN', 'PERSIARAN', 'LEBUH', 'LORONG', 'SOLOK' ];
      let streetPhrases = canonicalAddressTokens.reduce((phrases, word, index) => {
        if (streetPrefixes.includes(word) && canonicalAddressTokens[index + 1]) {
          phrases.push(`${word} ${canonicalAddressTokens[index + 1]}`);
        }
        return phrases;
      }, []);
      let addressWords = new Set(address.split(' ').filter(word =>
        word.length >= 3 && ![ 'THE', 'JALAN', 'JLN', 'LOT', 'SEKSYEN', 'SECTION',
          'KAWASAN', 'MALAYSIA', 'SELANGOR', 'PERAK' ].includes(word)
      ));
      let matchedData = [];

      (v['submittedList'] || []).forEach(submitted => {
        if (!submitted) return;
        let rawCode = String(submitted[v['submittedKey']] == null ? '' : submitted[v['submittedKey']]);
        let code = rawCode.trim().toUpperCase();
        let key = normalize(rawCode);
        let rawName = submitted[v['submittedNameKey'] || v['submittedDesc']];
        let name = normalize(rawName);
        let keyEncoding = encode(key);
        let nameEncoding = encode(name);
        let exactCode = input.codeIsVisible && !!input.code && code === input.code;
        let exactName = !!query && query === name;
        let fuseScore = fuseScores.has(submitted) ? fuseScores.get(submitted) : null;

        // Keep the original Soundex route. Add exact matches and a full
        // nonempty Metaphone-code match to recover phonetic alternatives.
        let candidates = v['compareByKey'] ? [keyEncoding] : [keyEncoding, nameEncoding];
        let phoneticMatch = candidates.some(candidate =>
          (detectedEncoding.soundex && candidate.soundex === detectedEncoding.soundex) ||
          (queryEncoding.soundex && candidate.soundex === queryEncoding.soundex) ||
          sameMetaphone(queryEncoding.metaphone, candidate.metaphone)
        );
        if (!exactCode && !exactName && !phoneticMatch && fuseScore == null) return;

        // Original weights: Levenshtein 10/10, Metaphone 20/10 for each
        // column, Jaro-Winkler 70. Compare the company line, not its address.
        let nameSimilarity = similarity(query, name);
        // Compare the submitted reference/code line with candidate codes.
        // Company similarity remains a separate signal.
        let score = similarity(detected, key) * 15 + nameSimilarity * 10;
        [keyEncoding, nameEncoding].forEach(candidate => {
          if (queryEncoding.metaphone[0] && queryEncoding.metaphone[0] === candidate.metaphone[0]) score += 20;
          if (queryEncoding.metaphone[1] && queryEncoding.metaphone[1] === candidate.metaphone[1]) score += 10;
        });
        score += input.codeIsVisible
          ? this.jaroWinkler(input.code, code) * 70
          : this.jaroWinkler(query, name) * 70;

        // JobFormComponent uses Fuse results before its Soundex fallback.
        // Keep phonetic scoring and add Fuse as a bounded supporting signal.
        if (fuseScore != null) score += (1 - fuseScore) * 10;

        let rawAddress = this.getSubmittedAddress(v, submitted);
        let candidateAddress = normalize(this.cleanMatchAddress(rawAddress, rawName, code));
        let addressScore = 0;
        let addressEvidence = 0;
        let strongCompanyMatch = exactName || nameSimilarity >= 0.55 ||
          (fuseScore != null && fuseScore <= 0.15);

        // JobFormComponent removes text after the first "(" before Fuse
        // matching. Branch suffixes such as "HAULAGE USE" therefore lower
        // raw similarity but should not prevent an address comparison.
        if (address && candidateAddress && strongCompanyMatch) {
          let a = new Set(address.split(' '));
          let b = new Set(candidateAddress.split(' '));
          let canonicalCandidateAddress = candidateAddress.split(' ')
            .map(word => word === 'JLN' ? 'JALAN' : word).join(' ');
          let common = [...a].filter(word => b.has(word)).length;
          addressScore = Math.max(similarity(address, candidateAddress), 2 * common / (a.size + b.size));
          if (numbers.length) {
            let commonNumbers = numbers.filter(number => b.has(number)).length;
            let commonPostcodes = postcodes.filter(postcode => b.has(postcode)).length;
            let commonSpecificWords = [...addressWords].filter(word => b.has(word)).length;
            let exactStreetPhrase = streetPhrases.some(phrase =>
              canonicalCandidateAddress.includes(phrase)
            );
            addressScore *= 0.5 + 0.5 * commonNumbers / numbers.length;

            // Exact postcode plus lot/road evidence is the clearest branch
            // identifier. It ranks above broad state/country-only addresses.
            if (commonPostcodes > 0 && commonNumbers >= 2 && commonSpecificWords >= 1) {
              addressEvidence = 3;
            } else if (exactStreetPhrase && commonSpecificWords >= 2) {
              // OCR can alter a postcode or unit number. An exact street
              // phrase plus other address words still identifies the branch.
              addressEvidence = 2;
            } else if (numbers.length >= 2 && commonNumbers === numbers.length &&
                commonSpecificWords >= 1 && addressScore >= 0.65) {
              addressEvidence = 2;
            } else if (numbers.length >= 2 && commonNumbers >= 2 &&
                addressScore >= 0.55) {
              addressEvidence = 1;
            }
          }
          score += addressScore * 8;
        }

        // Contact data is only a small tie-breaker. It never contributes to
        // address evidence or overrides code/company/address matching.
        let candidateContact = normalize(this.getSubmittedContact(v, submitted));
        let contactScore = contact && candidateContact ? similarity(contact, candidateContact) : 0;
        score += contactScore * 0.5;

        // History helps choose between already relevant candidates. A strong
        // company/Fuse match is required, and a supplied address must agree.
        // The bonus is capped below exact-code and branch-address priorities.
        let count = historyCounts[code] || 0;
        let historyBonus = 0;
        if (count && strongCompanyMatch && (!address || addressScore >= 0.65)) {
          historyBonus = Math.min(8, Math.log2(count + 1) * 1.5);
          score += historyBonus;
        }
        // Persist priority in the score as well as sort metadata. This keeps
        // branch ordering intact if another component later sorts by score.
        if (addressEvidence > 0) score += addressEvidence * 100;
        if (exactCode) score += 1000;

        let match = { ...submitted, matchScore: Math.round(score * 100) / 100 };
        Object.defineProperty(match, '_exactCodeMatch', { value: exactCode });
        Object.defineProperty(match, '_addressEvidence', { value: addressEvidence });
        Object.defineProperty(match, '_addressScore', { value: addressScore });
        Object.defineProperty(match, '_candidateAddress', { value: candidateAddress });
        Object.defineProperty(match, '_contactScore', { value: contactScore });
        Object.defineProperty(match, '_strongCompanyMatch', { value: strongCompanyMatch });
        Object.defineProperty(match, '_historyBonus', { value: historyBonus });
        matchedData.push(match);
      });

      matchedData.sort((a, b) =>
        Number(b['_exactCodeMatch']) - Number(a['_exactCodeMatch']) ||
        b['_addressEvidence'] - a['_addressEvidence'] ||
        b['matchScore'] - a['matchScore']
      );
      return matchedData.slice(0, 15);
    },
    selectBestMatch: function() {
      let currentSection = this.sections[this.currentSectionIndex];
      if (!currentSection) return;
      currentSection['verifyData'].forEach(v => {
        if (v['collisions'] && v['collisions'].length > 0) {
          let exactCodeMatch = v['collisions'].find(match => match['_exactCodeMatch']);
          let addressMatches = v['collisions'].filter(match => match['_addressEvidence'] > 0);
          addressMatches.sort((a, b) =>
            b['_addressEvidence'] - a['_addressEvidence'] ||
            b['_addressScore'] - a['_addressScore'] ||
            b['matchScore'] - a['matchScore']
          );

          // Exact supplied code remains authoritative. Otherwise a verified
          // branch address is selected before the general phonetic ranking.
          v['selectedObject'] = exactCodeMatch || addressMatches[0] || v['collisions'][0];
          v['errorMessage'] = '';
        }
      });
    },
    jaroWinkler: function(s1, s2) {
      s1 = String(s1 == null ? '' : s1);
      s2 = String(s2 == null ? '' : s2);
      if (!s1 || !s2) return 0;
      if (s1 === s2) return 1;

      let distance = Math.max(0, Math.floor(Math.max(s1.length, s2.length) / 2) - 1);
      let matched1 = new Array(s1.length).fill(false);
      let matched2 = new Array(s2.length).fill(false);
      let matches = 0;
      for (let i = 0; i < s1.length; i++) {
        for (let j = Math.max(0, i - distance); j < Math.min(s2.length, i + distance + 1); j++) {
          if (!matched2[j] && s1[i] === s2[j]) {
            matched1[i] = true;
            matched2[j] = true;
            matches++;
            break;
          }
        }
      }
      if (!matches) return 0;

      let left = s1.split('').filter((_, i) => matched1[i]);
      let right = s2.split('').filter((_, i) => matched2[i]);
      let transpositions = left.filter((character, i) => character !== right[i]).length / 2;
      let jaro = (matches / s1.length + matches / s2.length + (matches - transpositions) / matches) / 3;
      let prefix = 0;
      while (prefix < Math.min(4, s1.length, s2.length) && s1[prefix] === s2[prefix]) prefix++;
      return jaro > 0.7 ? jaro + prefix * 0.1 * (1 - jaro) : jaro;
    },
    matchedManualSelect: function(data, index) {
      let verifyData = this.sections[this.currentSectionIndex]['verifyData'][index];
      let compareKey = verifyData['submittedKey'];
      let collisions = verifyData['collisions'];

      if (data && collisions) {
        let hasMatched = collisions.find(c => c[compareKey] == data[compareKey]);
        if (hasMatched) {
          verifyData['selectedObject'] = hasMatched;
        }
      }
    },
    //#endregion

    //#region Sound Encoding
    listSoundex(list) {
      let soundexEncoding = [];
      for(let i = 0; i < list.length; i++) {
        let name = list[i];
        let code = this.soundex(name);
        soundexEncoding.push(code);
      }
      return soundexEncoding;
    },
    soundex(name) {
      let s = [];
      let si = 1;
      let c;
      let mappings = "01230120022455012623010202";
      try {
        s[0] = name[0].toUpperCase();
        for (let j=0; j < name.length; j++) {
          c = (name[j].toUpperCase()).charCodeAt(0) - 65;
          if (c >= 0 && c <= 25) {
            if (mappings[c] != '0') {
              if (mappings[c] != s[si - 1]) {
                s[si] = mappings[c];
                si++;
              }
              if (si > 3) {
                break;
              }
            }
          }
        }
        if (si <= 3) {
          while (si <= 3) {
            s[si] = 0;
            si++;
          }
        }
        s = s.join("");
      }
      catch(err) {
        s = "";
      }
      return s;
    },
    listDMetaphone(list) {
      let dMetaphoneEncoding = [];
      for(let i = 0; i < list.length; i++) {
        let name = list[i];
        let code = doubleMetaphone(name);
        dMetaphoneEncoding.push(code);
      }
      return dMetaphoneEncoding;
    },
    levenshtein(s, t) {
      if (s === t) {
          return 0;
      }
      var n = s.length, m = t.length;
      if (n === 0 || m === 0) {
          return n + m;
      }
      var x = 0, y, a, b, c, d, g, h, k;
      var p = new Array(n);
      for (y = 0; y < n;) {
          p[y] = ++y;
      }

      for (; (x + 3) < m; x += 4) {
          var e1 = t.charCodeAt(x);
          var e2 = t.charCodeAt(x + 1);
          var e3 = t.charCodeAt(x + 2);
          var e4 = t.charCodeAt(x + 3);
          c = x;
          b = x + 1;
          d = x + 2;
          g = x + 3;
          h = x + 4;
          for (y = 0; y < n; y++) {
              k = s.charCodeAt(y);
              a = p[y];
              if (a < c || b < c) {
                  c = (a > b ? b + 1 : a + 1);
              }
              else {
                  if (e1 !== k) {
                      c++;
                  }
              }

              if (c < b || d < b) {
                  b = (c > d ? d + 1 : c + 1);
              }
              else {
                  if (e2 !== k) {
                      b++;
                  }
              }

              if (b < d || g < d) {
                  d = (b > g ? g + 1 : b + 1);
              }
              else {
                  if (e3 !== k) {
                      d++;
                  }
              }

              if (d < g || h < g) {
                  g = (d > h ? h + 1 : d + 1);
              }
              else {
                  if (e4 !== k) {
                      g++;
                  }
              }
              p[y] = h = g;
              g = d;
              d = b;
              b = c;
              c = a;
          }
      }

      for (; x < m;) {
          var e = t.charCodeAt(x);
          c = x;
          d = ++x;
          for (y = 0; y < n; y++) {
              a = p[y];
              if (a < c || d < c) {
                  d = (a > d ? d + 1 : a + 1);
              }
              else {
                  if (e !== s.charCodeAt(y)) {
                      d = c + 1;
                  }
                  else {
                      d = c;
                  }
              }
              p[y] = d;
              c = a;
          }
          h = d;
      }

      return h;
    },
    //#endregion

    submittedInputQueryRefreshClicked: function(placeholder) {
      this.$emit("submittedRefreshClicked", placeholder);
    },
  },
  async mounted() {
    this.slotsCount = Object.keys(this.$slots).length;
    let allHistoricalValue = {};
    try {
      let loginDetails = await this.$apiCallV2('/Login/GetDetails');
      allHistoricalValue = await this.$apiCallV2(
        '/UserSelection/GetHistoricalValue?corefname=' + encodeURIComponent(loginDetails['CoRefname'])
      ) || {};
    } catch (e) {
      console.warn('Unable to load selection history; using phonetic matching.', e);
    }
    this.sections.forEach(s => {
      s['verifyData'].forEach(v => {
        this.$set(v, 'showCollisions', false);
        this.$set(v, 'tableStyling', {});
        this.$set(v, 'errorMessage', '');
        this.$set(v, 'selectedObject', null);
        this.$set(v, 'collisions', this.buildPhoneticMatches(v, allHistoricalValue));
      });
    });
    this.selectBestMatch();
  },
  watch: {
  }
}
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.verification-main {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(128,128,128,0.5);
  z-index: 999;
  padding: 10px 45px;

  .verification-main-content {
    height: 100%;
    width: 100%;
    background-color: white;
    display: flex;
    flex-direction: column;

    > * {
      padding: 5px;
    }

    > .header {
      position: relative;
      padding: 10px 5px;
      border-bottom: 2px solid gray;
      
      > .close-icon {
        height: 20px;
        width: 20px;
        position: absolute;
        right: 5px;
        top: 5px;
        cursor: pointer;
      }
    }

    > .body {
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      > .sections-indicator {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: space-around;

        > .section-number {
          border-radius: 100%;
          width: 30px;
          height: 30px;
          border: 2px solid gray;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      > .body-content {
        height: 100%;
        margin-top: 5px;
        display: flex;
        flex-direction: column;

        > .auto-select-button {
          align-self: flex-end;
        }

        > .body-details {
          display: flex;
          height: 100%;

          > .carousel-container {
            max-width: 35%;
            min-width: 35%;
            width: 35%;
            border: 1px solid gray;
            margin-right: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          > .row-sections {
            overflow: auto;
            width: 100%;

            > .row-items {
              display: flex;
              align-items: flex-start;
              background-color: rgba(211,211,211,0.5);
              margin: 3px 0;

              > .column {
                position: relative;
                width: 100%;
                min-width: calc((100% - 400px) / 2);
                max-width: calc((100% - 400px) / 2);
                padding: 5px;
                white-space: pre-wrap;

                > .score-bubble {
                  color: white;
                  padding: 3px 5px;
                  border-radius: 5px;
                  width: fit-content;
                  white-space: nowrap;
                }
              }

              > :first-child, > :last-child {
                max-width: 200px;
                min-width: 200px;
              }

              > .collision-button {
                display: flex;
                justify-content: flex-end;
                
                > button {
                  display: flex;
                  align-items: center;
                  position: relative;

                  > * {
                    pointer-events: none;
                  }

                  > img {
                    margin-left: 5px;
                    height: 20px;
                    width: 20px;
                    transition: 0.3s;
                  }
                }

              
              }

              > .center-text {
                text-align: center;
              }

              > .no-wrap {
                // white-space: nowrap;
              }
            }

            > .row-header {
              padding: 5px 0;
            }
          }
        }
      }
    }

    > .footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > * {
        margin-left: 5px;
      }
    }
  }
}

.attachments-section {
  height: 100%;
  min-width: 25%;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;

  > .attachment-details {
    width: 100%;
    background-color: white;
    padding: 5px;

    > .arrow-up {
      position: absolute;
      width: 30px;
      height: 20px;
      top: 0;
      left: calc(50% - 15px);
      transform: translateY(-100%);
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: white;
      border-color: gray;
      border-width: 1px 1px 0 1px;
      border-style: solid;
      cursor: pointer;
      user-select: none;

      > img {
        height: 100%;
        pointer-events: none;
        transition: 0.3s;
      }
    }
  }

  > .attachment-container {
    object-fit: contain;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    text-align: center;
    max-height: calc(100% - 90px);
  }
}
.collision-table {
  position: fixed;
  z-index: 151;
  width: 50vw;
  height: 50vh;
  padding: 5px;
  background-color: white;
  border: 1px solid gray;
}

> .collision-table-background {
  z-index: 149;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  position: fixed;
}
</style>